import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import AccessDenied from "@/pages/AccessDenied";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

export function IqGate({ children }) {
  const { user } = useAuth();
  const f = usePhaseFeature(8, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase8-gate-loading">Loading…</div>;
  if (!["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key)) return <AccessDenied />; // B2a: role check first → 403 page (no module-state leak)
  if (!f.enabled) return <NotFound />;
  return children;
}

const Shell = ({ title, subtitle, crumbs, children }) => {
  const { user } = useAuth();
  return <AdminAppShell activeNav="iq" user={user} title={title} subtitle={subtitle} breadcrumbs={crumbs}>{children}</AdminAppShell>;
};

const Cite = ({ i, prefix = "" }) => (i.link
  ? <Link to={i.link} className="inline-flex min-h-[44px] items-center underline" data-testid={`phase8-cite-${i.record_id}`}>{prefix}{i.label}</Link>
  : <span className="inline-flex min-h-[44px] items-center" data-testid={`phase8-cite-${i.record_id}`}>{prefix}{i.label} — {i.available_when}</span>);

const Draft = () => (
  <p className="mb-4 rounded-md border border-erf-gold/40 bg-erf-gold/10 px-4 py-3 text-sm font-nav text-erf-dark" data-testid="phase8-draft-note">
    Draft from fixed rules. It needs your review. Nothing is sent, approved or paid automatically. AI summaries are drafts until you approve them.
  </p>
);

function Weights({ r, reload, setError }) {
  const [w, setW] = useState(Object.fromEntries(Object.entries(r.weights).map(([k, v]) => [k, String(v)])));
  const save = () => api.put("/phase8/weights", Object.fromEntries(Object.entries(w).map(([k, v]) => [k, Number(v)])))
    .then(() => { setError(null); reload(); }).catch((e) => setError(errMsg(e, "Could not save weights.")));
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="phase8-weights">
      {Object.entries(r.signals).map(([k, label]) => (
        <label key={k} className="text-sm font-nav text-erf-dark">{label} (weight 0–10)
          <Input inputMode="decimal" value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} className="mt-1 h-11" data-testid={`phase8-weight-${k}`} />
        </label>
      ))}
      <div><Button variant="outline" className="h-11 border-erf-borderCream" onClick={save} data-testid="phase8-weights-save">Save weights</Button></div>
    </div>
  );
}

export function IqHome() {
  const [r, setR] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get("/phase8/risk").then((x) => setR(x.data));
    api.get("/phase8/briefs").then((x) => setBriefs(x.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const founder = r?.role_key === "OWNER_SUPER_ADMIN";
  const gen = () => api.post("/phase8/briefs").then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "Could not create the brief.")));
  return (
    <Shell title="Construction IQ" subtitle="Risk radar and weekly briefs built from your own project records."
      crumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Construction IQ", to: null }]}>
      <div data-testid="phase8-workspace">
        <Draft />
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase8-error">{error}</p>}
        {r && (
          <Card className="ds-card border-erf-borderCream mb-5" data-testid="phase8-radar">
            <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
              <h2 className="font-display font-semibold text-base md:text-lg">Risk radar</h2>
              {r.projects.length === 0 && <p>No open risk signals.</p>}
              <ul className="space-y-2">
                {r.projects.map((p) => (
                  <li key={p.project_id} data-testid={`phase8-risk-${p.project_id}`}>
                    <strong>{p.project_name}</strong> — score {p.score}
                    <ul className="mt-1 ml-4 list-disc space-y-1" data-testid={`phase8-explain-${p.project_id}`}>
                      {p.contributions.map((c) => (
                        <li key={c.signal}>{c.rule}: {c.count} × weight {c.weight} = {c.points} points
                          <ul className="ml-4">{c.evidence.map((e) => <li key={e.record_id}><Cite i={e} /></li>)}</ul>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {founder && <Weights key={JSON.stringify(r.weights)} r={r} reload={load} setError={setError} />}
            </CardContent>
          </Card>
        )}
        <Card className="ds-card border-erf-borderCream" data-testid="phase8-briefs">
          <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
            <h2 className="font-display font-semibold text-base md:text-lg">Weekly briefs</h2>
            {founder && <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={gen} data-testid="phase8-generate">Create draft brief</Button>}
            <ul className="space-y-1">
              {briefs.length === 0 && <li>No briefs yet.</li>}
              {briefs.map((b) => (
                <li key={b.id}><Link to={`/phase8/briefs/${b.id}`} className="inline-flex min-h-[44px] items-center underline" data-testid={`phase8-brief-link-${b.id}`}>Week of {b.week_of} · {b.state}</Link></li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}

export function BriefDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase8/briefs/${id}`).then((r) => setB(r.data)).catch((e) => setError(errMsg(e, "Could not load."))), [id]);
  useEffect(() => { load(); }, [load]);
  const approve = () => api.post(`/phase8/briefs/${id}/approve`).then(load).catch((e) => setError(errMsg(e, "Could not approve.")));
  const [busy, setBusy] = useState(false);
  const draftAi = () => { setBusy(true); api.post(`/phase8/briefs/${id}/ai-summary`).then(load).catch((e) => setError(errMsg(e, "The AI draft could not be created."))).finally(() => setBusy(false)); };
  const approveAi = () => api.post(`/phase8/briefs/${id}/ai-summary/approve`).then(load).catch((e) => setError(errMsg(e, "Could not approve.")));
  return (
    <Shell title={b ? `Brief · week of ${b.week_of}` : "Brief"} subtitle={b ? (b.state === "approved" ? "Approved for internal use" : "Draft — needs Founder review") : ""}
      crumbs={[{ label: "Construction IQ", to: "/phase8/iq" }, { label: "Brief", to: null }]}>
      <div data-testid="phase8-brief-detail">
        <Draft />
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase8-brief-error">{error}</p>}
        {b && (
          <Card className="ds-card border-erf-borderCream">
            <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
              <p data-testid="phase8-brief-summary">{b.summary}</p>
              {b.ai_summary && (
                <section className="rounded-lg border border-erf-gold/40 p-3" data-testid="phase8-ai-summary">
                  <h2 className="font-display font-semibold text-base md:text-lg">AI summary · {b.ai_summary.state === "approved" ? "approved by Founder" : "draft, needs your approval"}</h2>
                  <p className="text-xs text-erf-dark/80">Every sentence links to the records it is based on. Claims that cite anything else are rejected automatically.</p>
                  <ul className="mt-2 space-y-2">
                    {b.ai_summary.claims.map((c, n) => (
                      <li key={n} data-testid={`phase8-ai-claim-${n}`}>{c.text}
                        <ul className="ml-4">{c.citations.map((ci) => <li key={ci.record_id}><Cite i={ci} /></li>)}</ul>
                      </li>
                    ))}
                  </ul>
                  {b.ai_summary.state === "draft" && b.role_key === "OWNER_SUPER_ADMIN" && <Button variant="outline" className="mt-2 h-11 border-erf-borderCream" onClick={approveAi} data-testid="phase8-ai-approve">Approve AI summary</Button>}
                </section>
              )}
              {b.role_key === "OWNER_SUPER_ADMIN" && !b.ai_summary && <Button variant="outline" disabled={busy} className="h-11 border-erf-borderCream" onClick={draftAi} data-testid="phase8-ai-draft">{busy ? "Drafting…" : "Draft AI summary"}</Button>}
              {b.sections.map((s) => (
                <section key={s.signal}>
                  <h2 className="font-display font-semibold text-base md:text-lg">{s.title} ({s.count})</h2>
                  <ul className="mt-1 space-y-1">
                    {s.items.map((i) => (
                      <li key={i.record_id}><Cite i={i} prefix={`${i.project_name}: `} /></li>
                    ))}
                  </ul>
                </section>
              ))}
              {b.state === "draft" && b.role_key === "OWNER_SUPER_ADMIN" && <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={approve} data-testid="phase8-approve">Approve for internal use</Button>}
            </CardContent>
          </Card>
        )}
      </div>
    </Shell>
  );
}
